/**
 * The top-line "how is the fly doing right now" summary strip
 * (design/reshape/RESHAPE.md §2, requested in the DESIGNER brief) — pure
 * derivation from data this app already fetches (`role.events()` +
 * `role.performance()`), no new snapshot fields, no new network calls.
 *
 * The alive/session derivation deliberately mirrors
 * `src/brain/sessionStatus.ts`'s `deriveSessionStatus` branch-for-branch
 * (same known gap: `life.assessment.sleep_state` can read "awake" right
 * after a restart during a closed session, per README.md — so this never
 * reads `sleep_state`, only the same two real event kinds that function
 * already trusts) rather than reimplementing new judgment calls, so the
 * summary strip's "alive/asleep" badge can never disagree with the brain
 * panel's own resting-state banner. `latestByKind`/`STATE_LABELS` are
 * *imported* from `sessionStatus.ts`, not re-copied here — a round-2
 * self-review finding caught that an earlier draft's copy-pasted version
 * had already drifted on what counts as "a wake" (see `latestWake` below),
 * so sharing the one implementation is the actual fix, not just matching
 * the label text.
 */

import type { LogEvent } from "../types/events";
import type { PerformanceDocument } from "../types/incremental";
import { currentSessionSignal, latestByKind, STATE_LABELS } from "../brain/sessionStatus";

export type AliveState = "awake" | "asleep" | "unknown";

export interface SummaryState {
  readonly alive: AliveState;
  readonly sessionLabel: string;
  readonly nav: string | null;
  readonly unrealizedPnl: string | null;
  readonly realizedPnlCum: string | null;
  readonly openPositions: number | null;
  readonly openOrders: number | null;
  readonly lastDecisionEventTime: string | null;
  readonly lastDecisionMode: string | null;
}

function stringField(
  payload: Readonly<Record<string, unknown>> | undefined,
  key: string,
): string | null {
  const value = payload?.[key];
  return typeof value === "string" ? value : null;
}

function numberField(
  payload: Readonly<Record<string, unknown>> | undefined,
  key: string,
): number | null {
  const value = payload?.[key];
  return typeof value === "number" ? value : null;
}

function arrayLength(
  payload: Readonly<Record<string, unknown>> | undefined,
  key: string,
): number | null {
  const value = payload?.[key];
  return Array.isArray(value) ? value.length : null;
}

export function buildSummaryState(
  events: readonly LogEvent[],
  performance: Pick<PerformanceDocument, "nav" | "unrealized_pnl" | "realized_pnl_cum"> | null,
): SummaryState {
  // FEM: one shared resolver with `deriveSessionStatus` (src/brain/sessionStatus.ts)
  // — the session falls back to the latest wake's own `session_state` once the
  // (24/7 crypto parent's single) transition ages out of the published window,
  // and a rejection only counts when no wake has happened since it. Its wake
  // filter matches src/brain/wakeTimeline.ts's buildWakeTimelineFromEvents
  // exactly, so this can never disagree with BrainPanel's own check.
  const { transition, rejection, latestWake, toState } = currentSessionSignal(events);
  const latestSnapshot = latestByKind(events, "portfolio.snapshot");

  let alive: AliveState;
  let sessionLabel: string;
  if (!transition && !rejection && !toState) {
    // No session signal at all (e.g. a v1 fixture, or nothing has run yet).
    // A recorded wake is still real evidence of activity, so say "awake"
    // rather than "unknown" — but never guess a session label without a
    // real transition event.
    alive = latestWake ? "awake" : "unknown";
    sessionLabel = "unknown";
  } else if (latestWake && toState === "regular" && !rejection) {
    alive = "awake";
    sessionLabel = "regular session";
  } else {
    alive = "asleep";
    sessionLabel = toState ? (STATE_LABELS[toState] ?? toState) : "unknown";
  }

  return {
    alive,
    sessionLabel,
    nav: performance?.nav ?? null,
    unrealizedPnl: performance?.unrealized_pnl ?? null,
    realizedPnlCum: performance?.realized_pnl_cum ?? null,
    openPositions: arrayLength(latestSnapshot?.payload, "positions"),
    openOrders: numberField(latestSnapshot?.payload, "open_orders"),
    lastDecisionEventTime: latestWake?.eventTime ?? null,
    lastDecisionMode: stringField(latestWake?.payload, "mode"),
  };
}
